import { eFontAsset, ePublisherAPI, Game } from "../../Game";
import { eSprite, SPRITES } from "../../Sprites";
import { DebugOutput } from "../../Utils/DebugOutput";
import { eGameplayState, Gameplay, GameResults } from "../Gameplay";
import { RoundManager } from "../Round/RoundManager";
import { AnimMessageManager } from "./AnimMessages/AnimMessageManager";
import { Results } from "./Arc8/Results";
import { StatusBar as Arc8StatusBar } from "./Arc8/Status bar/StatusBar";
import { StatusBar as PrizesStatusBar } from "./Prizes/Status bar/StatusBar";
import { CharCard } from "./CharCard";
import { FlashFxManager } from "./FlashFx";
import { ManCityIntro } from "./ManCityIntro";
import { ScreenOverlay } from "./ScreenOverlay";
import { Tutorial } from "./Tutorial";



export class HUD {
    static get instance(): HUD { return HUD._instance; }
    private static _instance: HUD;

    /**
     * (RO) pc.Entity se Screen komponentou pro zobrazeni informaci v HUDu
     */
    get screen(): pc.Entity { return this._screen; }
    private _screen: pc.Entity;

    get staticUIBatchGroupId(): number { return this._staticUIBatchGroupId; }
    private _staticUIBatchGroupId: number;

    /**
     * (RO) Karticka postavy
     */
    get charCard(): CharCard { return this._charCard; }
    private _charCard: CharCard;

    /**
     * (RO) Animovana zprava
     */
    get animMessage(): AnimMessageManager { return this._animMessage; }
    private _animMessage: AnimMessageManager;

    get tutorial(): Tutorial { return this._tutorial; }
    private _tutorial: Tutorial;

    private _statusBar: Arc8StatusBar | PrizesStatusBar;
    private _results: Results;

    get manCityIntro(): ManCityIntro { return this._manCityIntro; }
    private _manCityIntro: ManCityIntro;

    /**
     * (RO) Overlay pres celou obrazovku
     */
    get overlay(): ScreenOverlay { return this._overlay; }
    private _overlay: ScreenOverlay;

    /**
     * (RO) Efekt zablesku
     */
    get flashFx(): FlashFxManager { return this._flashFx; }
    private _flashFx: FlashFxManager;

    //info: pc.Entity;

    debugOutput: DebugOutput;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        HUD._instance = this;

        this._staticUIBatchGroupId = Game.app.batcher.addGroup("staticUI", false, 100).id;

        // 2D screen
        let scale = Game.scale;

        this._screen = new pc.Entity("hud");
        this._screen.addComponent(pc.eCompomentType.screen, {
            scaleMode: pc.SCALEMODE_BLEND,
            screenSpace: true,
            scaleBlend: scale.maxWidth < scale.maxHeight ? 0 : 1,
            referenceResolution: new pc.Vec2(scale.minWidth, scale.minHeight)
        });
        Game.app.root.addChild(this._screen);

        // Karticka postavy hrace
        this._charCard = new CharCard(this._screen);

        // Animovane zpravy
        this._animMessage = new AnimMessageManager(this._screen);

        // Tutorial
        if (Game.settings.tutorial)
            this._tutorial = new Tutorial(this._screen);

        // Efekt zablesku
        this._flashFx = new FlashFxManager(this._screen);

        // Status bar
        this._statusBar = (Game.API != ePublisherAPI.PRIZES ? new Arc8StatusBar(this._screen) : new PrizesStatusBar(this._screen));

        // Screen overlay
        this._overlay = new ScreenOverlay(this._screen);

        // ManCity intro
        this._manCityIntro = new ManCityIntro(this._screen, this._overlay);

        // Vysledek hry
        if (Game.API == ePublisherAPI.ARC8)
            this._results = new Results(this._screen);

        // Zachytavat zahajeni kola hry
        Gameplay.instance.events.on(RoundManager.EVENT_ROUND_READY, this.handleRoundReady, this);

        //this.debugOutput = new DebugOutput(this._screen, 10, -100, 20, Game.app.assets.find(eFontAsset.default, pc.eAssetType.font).id, 20, undefined, true);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        //this.debugOutput.clear();

        if (this._tutorial)
            this._tutorial.reset();

        this._animMessage.reset();
        this._flashFx.reset();
        this._statusBar.reset();

        if (this._results)
            this._results.reset();

        this._manCityIntro.reset();
        this._charCard.reset();
        this._overlay.enabled = false;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): void {
        this._statusBar.update();
        this._flashFx.update();
        this._animMessage.update();
        this._charCard.update();
        this._manCityIntro.update();

        if (this._results)
            this._results.update();

        if (this._tutorial)
            this._tutorial.update();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    showResults(res: GameResults): void {
        this._statusBar.visible = false;

        if (this._tutorial)
            this._tutorial.reset();

        this._manCityIntro.reset();
        this._overlay.enabled = true;
        this._overlay.opacity = 0.5;
        this._results.show(res);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    handleRoundReady(): void {
        // Zobrazeni tutorialu
        if (Game.settings.tutorial && Gameplay.instance.state == eGameplayState.play) {
            let tutSet = RoundManager.instance.roundSetup.tutorialSettings;

            if (tutSet)
                this._tutorial.show(tutSet);
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    static createButton(sprite: eSprite, width: number, height: number, anchor: pc.Vec4, pivot: pc.Vec2, parent: pc.GraphNode, pos: pc.Vec3, batchGroupId: number = -1): pc.Entity {
        let asset = Game.app.assets.find(SPRITES[sprite].name, pc.eAssetType.sprite);
        let entity = new pc.Entity();

        entity.addComponent(pc.eCompomentType.element, {
            type: pc.ELEMENTTYPE_IMAGE,
            width: width,
            height: height,
            anchor: anchor,
            pivot: pivot,
            useInput: true,
            spriteAsset: asset.id,
            batchGroupId: batchGroupId
        });

        entity.addComponent(pc.eCompomentType.button, {
            active: true,
            imageEntity: entity,
            transitionMode: pc.BUTTON_TRANSITION_MODE_SPRITE_CHANGE,
            inactiveSpriteAsset: asset,
            hoverSpriteAsset: asset,
            pressedSpriteAsset: asset,
        });

        entity.button.hoverSpriteFrame = 0;
        entity.button.pressedSpriteFrame = 1;
        entity.button.on("mousedown", (event: pc.MouseEvent) => { event.event.stopImmediatePropagation(); });
        entity.button.on("touchstart", (event: pc.MouseEvent) => { event.event.stopImmediatePropagation(); });

        parent.addChild(entity);
        entity.setLocalPosition(pos);

        return entity;
    }
}